import { SKETCH_TEMPLATE_LABELS } from "@/components/sketch/SketchTemplateUnderlay";

export default function TrainingSessionDetailPanel({
  session,
  busy,
  practicalEnabled,
  onTransition,
  onViewSketch,
}) {
  if (!session) return null;

  return (
    <div className="text-sm mt-3 space-y-3 border-t pt-3" data-testid={`session-detail-${session.record_id}`}>
      <div>
        <h4 className="font-semibold">Training session</h4>
        <p className="text-xs" style={{ color: "rgba(22,25,28,.6)" }}>
          {session.date_nz}
          {session.time_display ? ` · ${session.time_display}` : ""} · Mode {session.training_mode || "A"} · Rev{" "}
          {session.current_revision}
          {session.saved_at_nz ? ` · Saved ${session.saved_at_nz}` : ""}
          {session.review_required ? " · Review required (date/timestamp mismatch)" : ""}
        </p>
        <p>Instructor: {session.instructor_name || "—"}</p>
        {session.operational_lesson_id && (
          <p data-testid="operational-lesson-link">
            Operational GDI lesson ID: <code>{session.operational_lesson_id}</code>
          </p>
        )}
      </div>

      {(session.daily_task_assessments?.length > 0 ||
        session.competency_assessments?.length > 0 ||
        session.custom_category_assessments?.length > 0) && (
        <div data-testid="session-scores">
          <h4 className="font-semibold">Scores and notes</h4>
          <ul className="space-y-1">
            {(session.daily_task_assessments || []).map((a) => (
              <li key={a.task_id}>
                {a.label || a.task_id}: {typeof a.score === "number" ? a.score : "N/A"}
                {a.note ? ` — ${a.note}` : ""}
              </li>
            ))}
            {(session.competency_assessments || []).map((a) => (
              <li key={a.competency_id}>
                {a.competency_id}: {typeof a.score === "number" ? a.score : "N/A"}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h4 className="font-semibold">Strengths</h4>
        <p>{session.strengths || "—"}</p>
      </div>
      <div>
        <h4 className="font-semibold">Practice areas</h4>
        <p>{session.areas_for_improvement || "—"}</p>
      </div>
      <div>
        <h4 className="font-semibold">Next session focus</h4>
        <p>{session.next_session_focus || "—"}</p>
      </div>

      <div data-testid="session-private-notes">
        <h4 className="font-semibold">Private instructor notes</h4>
        {session.private_notes_redacted ? (
          <p className="text-xs italic" style={{ color: "rgba(22,25,28,.6)" }}>
            Redacted — not authored by you
          </p>
        ) : (
          <p>{session.instructor_notes_private || "—"}</p>
        )}
      </div>

      {session.sketches?.length > 0 && (
        <div data-testid="session-sketches">
          <h4 className="font-semibold">Sketch attachments</h4>
          <ul className="space-y-2">
            {session.sketches.map((sk) => (
              <li key={sk.sketch_id} className="border rounded p-2">
                <p>
                  {sk.title || "Sketch"} · {sk.page_count} page{sk.page_count === 1 ? "" : "s"} · rev {sk.revision}
                  {sk.saved_at_nz ? ` · ${sk.saved_at_nz}` : ""}
                </p>
                <p className="text-xs" style={{ color: "rgba(22,25,28,.6)" }}>
                  {SKETCH_TEMPLATE_LABELS[sk.template_id] || sk.template_id}
                </p>
                {(sk.pages || []).map((p) => (
                  <p key={p.page_id} className="text-xs">
                    Page {p.page_index + 1}
                    {p.title ? `: ${p.title}` : ""}
                  </p>
                ))}
                {sk.access === "full" ? (
                  <button
                    type="button"
                    className="audit-btn-compact mt-1"
                    style={{ marginLeft: 0 }}
                    onClick={() => onViewSketch?.(sk)}
                    data-testid={`view-sketch-${sk.sketch_id}`}
                  >
                    View full sketch
                  </button>
                ) : (
                  <p className="text-xs italic mt-1">Metadata only — another instructor&apos;s sketch</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {(session.publication_state === "DRAFT" || !session.publication_state) && (
          <button
            type="button"
            className="audit-btn-compact"
            style={{ marginLeft: 0 }}
            disabled={busy || !practicalEnabled}
            onClick={() => onTransition(session.record_id, "approve")}
            data-testid={`approve-${session.record_id}`}
          >
            Approve
          </button>
        )}
        {session.publication_state === "APPROVED" && (
          <button
            type="button"
            className="audit-btn-publish"
            style={{ minHeight: "2.75rem", padding: "0 1rem" }}
            disabled={busy || !practicalEnabled}
            onClick={() => onTransition(session.record_id, "publish")}
            data-testid={`publish-${session.record_id}`}
          >
            Publish
          </button>
        )}
      </div>
    </div>
  );
}
